import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { TASK_TYPES, can, type TaskType } from '@mybusinessmate/schema';
import { TaskFormPage } from '../../../../../../../components/tasks/task-form-page';
import { PageSkeleton } from '../../../../../../../components/ui/skeleton';
import { LoadFailure, Forbidden } from '../../../../../../../components/ui/state-views';
import { projectQuery } from '../../../../../../../lib/projects-api';
import { useSession } from '../../../../../../../lib/session';
import { isStaffRoleName } from '../../../../../../../lib/staff-nav';

export const Route = createFileRoute('/subdomain/admin/_staff/projects/$projectId/tasks/new')({
  validateSearch: (search: Record<string, unknown>): { type: TaskType } => ({
    type: (TASK_TYPES as readonly unknown[]).includes(search.type) ? (search.type as TaskType) : 'post',
  }),
  // The form is 1200 wide in the design: the brief on the left, triage and the client side on the right.
  staticData: { wide: true },
  component: NewTaskPage,
});

function NewTaskPage() {
  const { projectId } = Route.useParams();
  const { type } = Route.useSearch();
  const { data: session } = useSession();
  const role = session?.user.role;
  const { data: project, isLoading, error, refetch } = useQuery(projectQuery(projectId));

  // Only admins and the business's account manager create tasks (the API enforces it; this just shows the right page).
  if (isStaffRoleName(role) && !can({ role }, 'task.create', { managesBusiness: role === 'account_manager' })) {
    return (
      <div className="rounded-(--mbm-component-radius-card) border border-border-line bg-surface-card">
        <Forbidden title="You can’t create tasks" description="Ask an account manager or an admin to add this one." />
      </div>
    );
  }

  if (isLoading) return <PageSkeleton />;

  if (!project) {
    return (
      <LoadFailure
        error={error}
        noun="project"
        forbiddenHint="Ask an admin to assign you to it."
        onRetry={() => void refetch()}
      />
    );
  }

  return <TaskFormPage project={project} initialType={type} />;
}
